import { Divider, Tab, Tabs } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { List } from "@react-admin/ra-rbac";
import { Fragment, useCallback } from "react";
import { Button, Datagrid, DatagridProps, Identifier, ListContextProvider, ListProps, TextField, TextInput, useDataProvider, useListContext, useNotify, useRecordContext, useRefresh, useTranslate } from "react-admin";
import { AvatarField } from "../common/components";
import { create, findOne } from "../services/common/common";
import { getFilterValues, useGetTotals } from "../services/project/delegate";

interface ProjectDelegateListProps extends ListProps {
    projectId: Identifier
};

interface ProjectDelegateDatagridProps extends DatagridProps {
    projectId: Identifier
};

const addTeamMember = (notify, refresh, dataProvider, projectId, userId) => () => {
    findOne(dataProvider, "project_delegate", {
        userId,
        projectId
    }).then((result) => {
        if (!result) {
            create(dataProvider, "project_delegate", {
                userId,
                projectId
            }).then(() => {
                refresh();
                notify("resources.project_delegate.add");
            });
        }
    });
};

const removeTeamMember = (notify, refresh, dataProvider, projectId, userId) => () => {
    findOne(dataProvider, "project_delegate", {
        userId,
        projectId
    }).then((result) => {
        const id = result?.id;
        if (id) {
            dataProvider.delete("project_delegate", { id }).then(() => {
                refresh();
                notify("resources.project_delegate.remove");
            })
        }
    });
};

const Action = ({ tab, projectId }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const record = useRecordContext();
    const buttonProps = tab === "others" ? {
        label: "resources.add",
        className: "activate",
        onClick: addTeamMember(notify, refresh, dataProvider, projectId, record.id)
    } : {
        label: "resources.remove",
        className: "deactivate",
        onClick: removeTeamMember(notify, refresh, dataProvider, projectId, record.id)
    };
    return <Button {...buttonProps} variant="contained">{tab === "others" ? <PersonAddIcon /> : <DeleteIcon />}</Button>
}

const TabbedDatagrid = (propsReceive: ProjectDelegateDatagridProps) => {
    const { projectId, ...props } = propsReceive;
    const translate = useTranslate();
    const listContext = useListContext();
    const { filterValues, setFilters, displayedFilters } = listContext;
    const handleChange = useCallback((event: React.ChangeEvent<{}>, value?: string) => {
        let f = getFilterValues(filterValues, value, projectId);
        setFilters && setFilters(f, displayedFilters);
    }, [filterValues, setFilters, displayedFilters, projectId]);
    const totals = useGetTotals(filterValues, projectId);
    return (
        <Fragment>
            <Tabs variant="fullWidth" value={filterValues.project_delegates !== undefined ? "mydelegate" : "others"} onChange={handleChange}>
                <Tab label={`${translate("resources.project_delegate.mydelegate")} (${totals.mydelegate})`} value="mydelegate" />
                <Tab label={`${translate("resources.project_delegate.others")} (${totals.others})`} value="others" />
            </Tabs>
            <Divider />
            <ListContextProvider value={{ ...listContext }}>
                <Datagrid {...props}>
                    <AvatarField source="fullname" />
                    <TextField source="email" />
                    <Action tab={filterValues.project_delegates !== undefined ? "mydelegate" : "others"} projectId={projectId} />
                </Datagrid>
            </ListContextProvider>
        </Fragment>
    );
};

export const ProjectDelegateList = (propsReceive: ProjectDelegateListProps) => {
    const { projectId, ...propsOthers } = propsReceive;
    const baseProps = {
        resource: "user",
        basePath: "/user"
    }
    const props = {
        ...propsOthers,
        ...baseProps,
        title: " ",
        hasCreate: false,
        bulkActionButtons: false
    };
    return <List {...props}
        filterDefaultValues={getFilterValues({ role: "projectManager" }, "mydelegate", projectId)}
        filters={[
            <TextInput label="resources.user.fields.fullname" source="fullname" alwaysOn />
        ]}>
        <TabbedDatagrid {...baseProps} projectId={projectId} />
    </List>;
};